import React from 'react';
import { motion } from 'framer-motion';
import Button from '../ui/Button';
import Card from '../ui/Card';
import { getCurrentBranding } from '../../config/branding';
import {
  Brain,
  FileText,
  Shield,
  Zap,
  ArrowRight,
  Sparkles,
  CheckCircle
} from 'lucide-react';

export interface HeroProps {
  title?: string;
  subtitle?: string;
  description?: string;
  primaryAction?: {
    label: string;
    onClick: () => void;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
}

const Hero: React.FC<HeroProps> = ({
  title,
  subtitle,
  description,
  primaryAction,
  secondaryAction
}) => {
  const branding = getCurrentBranding();
  
  // Use branding defaults if no props provided
  const displayTitle = title || branding.tagline;
  const displaySubtitle = subtitle || "Transform Legal Document Understanding";
  const displayDescription = description || branding.description;
  const features = [
    { icon: Brain, text: "AI-Powered Analysis", color: "text-blue-500" },
    { icon: Shield, text: "Secure & Compliant", color: "text-green-500" },
    { icon: Zap, text: "Lightning Fast", color: "text-purple-500" },
    { icon: FileText, text: "Multi-Format Support", color: "text-orange-500" }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background with gradient and floating elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="absolute top-20 left-20 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
        <div className="absolute top-40 right-20 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-40 w-72 h-72 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          {/* Badge */}
          <motion.div variants={itemVariants} className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 text-blue-800 text-sm font-medium">
            <Sparkles className="w-4 h-4 mr-2" />
            Powered by Advanced AI
          </motion.div>

          {/* Main Title */}
                             <motion.h1
                     variants={itemVariants}
                     className="text-5xl md:text-7xl font-bold text-gray-900 leading-tight"
                   >
                     {displayTitle.split(' ').map((word, index) => (
                       <span key={index} className="inline-block">
                         {word}
                         {index < displayTitle.split(' ').length - 1 && <span className="text-blue-600"> </span>}
                       </span>
                     ))}
                   </motion.h1>

          {/* Subtitle */}
                             <motion.p
                     variants={itemVariants}
                     className="text-xl md:text-2xl text-blue-600 font-semibold"
                   >
                     {displaySubtitle}
                   </motion.p>

                   {/* Description */}
                   <motion.p
                     variants={itemVariants}
                     className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
                   >
                     {displayDescription}
                   </motion.p>

          {/* Action Buttons */}
          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            {primaryAction && (
              <Button
                size="lg"
                onClick={primaryAction.onClick}
                rightIcon={<ArrowRight className="w-5 h-5" />}
                className="text-lg px-8 py-4"
              >
                {primaryAction.label}
              </Button>
            )}
            
            {secondaryAction && (
              <Button
                variant="outline"
                size="lg"
                onClick={secondaryAction.onClick}
                className="text-lg px-8 py-4"
              >
                {secondaryAction.label}
              </Button>
            )}
          </motion.div>

          {/* Features Grid */}
          <motion.div 
            variants={itemVariants}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto mt-12"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="text-center group"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110 mb-4">
                  <feature.icon className={`w-8 h-8 ${feature.color}`} />
                </div>
                <p className="text-sm font-medium text-gray-700">{feature.text}</p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Floating Stats Card */}
      <motion.div
        initial={{ opacity: 0, x: 100 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.8, duration: 0.6 }}
        className="absolute right-8 top-1/2 transform -translate-y-1/2 hidden lg:block"
      >
        <Card glass className="p-6 text-center">
          <div className="space-y-4">
            <div className="text-3xl font-bold text-blue-600">99.8%</div>
            <div className="text-sm text-gray-600">Accuracy Rate</div>
            <div className="flex items-center justify-center text-green-500">
              <CheckCircle className="w-5 h-5 mr-2" />
              <span className="text-sm">AI Verified</span>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.6 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-gray-400 rounded-full flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1 h-3 bg-gray-400 rounded-full mt-2"
          />
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Hero;
